import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Cpu, Lock, MapPin, Radio, Tag, X } from 'lucide-react'
import {
  type AssetPeripheral,
  type PeripheralCapability,
  peripheralCapabilityLabel,
  peripheralCapabilityToWindowId,
} from '../services/assetPeripheralService'

// =============================================================================
// AssetPeripheralPicker — modal shown when an asset has 2+ attached
// peripherals (any brand), asking the user which one(s) to open.
// =============================================================================
//
// Two interaction modes (P8 spec):
//   1) Click a card body  -> open that single peripheral's window and close.
//   2) Tick checkboxes    -> footer "Open" button appears once at least one
//                            card is checked; clicking it opens every checked
//                            peripheral and applies the multi-tile preset.
//
// Closing without picking (Esc, backdrop click, header X) is a no-op for
// the asset-centric workspace; the existing asset detail/Fleet Map remain
// as before.
// =============================================================================

export interface PickerPayload {
  assetId: string
  imei: string
  capability: PeripheralCapability
  windowId: string
  peripheral: AssetPeripheral
}

export interface AssetPeripheralPickerProps {
  assetId: string
  peripherals: AssetPeripheral[]
  onClose: () => void
  onOpenSingle: (payload: PickerPayload) => void
  onOpenMany: (payloads: PickerPayload[]) => void
}

function capabilityIcon(c: PeripheralCapability | string): React.ReactNode {
  switch (c) {
    case 'SMART_LOCK':  return <Lock className="w-5 h-5" />
    case 'AI_DASHCAM':  return <Camera className="w-5 h-5" />
    case 'OBD':         return <Cpu className="w-5 h-5" />
    case 'TAG':         return <Tag className="w-5 h-5" />
    case 'GPS_TRACKER': return <MapPin className="w-5 h-5" />
    default:            return <Radio className="w-5 h-5" />
  }
}

function relTime(iso?: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return '—'
  const ageSec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (ageSec < 60)    return `${ageSec}s`
  if (ageSec < 3600)  return `${Math.floor(ageSec / 60)}m`
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h`
  return `${Math.floor(ageSec / 86400)}d`
}

function isPeripheralOnline(p: AssetPeripheral): boolean {
  const ts = p.lastSeen ? new Date(p.lastSeen).getTime() : NaN
  if (!Number.isFinite(ts)) return false
  return Date.now() - ts < 5 * 60 * 1000
}

function payloadFor(assetId: string, p: AssetPeripheral): PickerPayload | null {
  if (!p.imei) return null
  return {
    assetId,
    imei: p.imei,
    capability: p.capability,
    windowId: peripheralCapabilityToWindowId(p.capability),
    peripheral: p,
  }
}

const AssetPeripheralPicker: React.FC<AssetPeripheralPickerProps> = ({
  assetId, peripherals, onClose, onOpenSingle, onOpenMany,
}) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  // Esc -> close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const sorted = useMemo(() => {
    const order: Record<string, number> = { GPS_TRACKER: 0, SMART_LOCK: 1, AI_DASHCAM: 2, OBD: 3, TAG: 4 }
    return [...peripherals].sort((a, b) => (order[a.capability] ?? 99) - (order[b.capability] ?? 99))
  }, [peripherals])

  const handleCardClick = useCallback((p: AssetPeripheral) => {
    const payload = payloadFor(assetId, p)
    if (payload) onOpenSingle(payload)
  }, [assetId, onOpenSingle])

  const handleOpenChecked = useCallback(() => {
    const payloads = sorted
      .filter((p) => selected.has(p.id))
      .map((p) => payloadFor(assetId, p))
      .filter((x): x is PickerPayload => x !== null)
    if (payloads.length === 0) return
    onOpenMany(payloads)
  }, [sorted, selected, assetId, onOpenMany])

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-[640px] max-w-[92vw] max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('peripheral_picker_title', 'Open peripheral tracking')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {t('peripheral_picker_subtitle', 'This asset has multiple peripherals attached. Click a card to open one, or tick checkboxes to open several.')}
            </p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={onClose}
            aria-label={t('peripheral_picker_close', 'Close') as string}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {sorted.length === 0 && (
            <div className="text-sm text-gray-500 italic text-center py-8">
              {t('peripheral_picker_empty', 'No peripherals attached to this asset.')}
            </div>
          )}
          {sorted.map((p) => {
            const isChecked = selected.has(p.id)
            const online = isPeripheralOnline(p)
            const disabled = !p.imei
            return (
              <div
                key={p.id}
                className={`group relative rounded-xl border transition-all ${
                  isChecked
                    ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/50 dark:bg-blue-500/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500'
                } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
              >
                {/* Top-right checkbox: stops propagation so toggling doesn't open. */}
                <label
                  className="absolute top-3 right-3 inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => toggle(p.id)}
                    aria-label={t('peripheral_picker_select_aria', 'Select peripheral') as string}
                  />
                </label>

                {/* Card body — single click opens the window. */}
                <button
                  type="button"
                  className="w-full text-left p-4 pr-12"
                  disabled={disabled}
                  onClick={() => !disabled && handleCardClick(p)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 flex items-center justify-center">
                      {capabilityIcon(p.capability)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {p.deviceName || peripheralCapabilityLabel(p.capability)}
                        </span>
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${online ? 'bg-emerald-500' : 'bg-gray-400'}`}
                          title={online ? t('peripheral_picker_online', 'Online') as string : t('peripheral_picker_offline', 'Offline') as string}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="uppercase tracking-wide">{peripheralCapabilityLabel(p.capability)}</span>
                        {p.brand && <span className="uppercase tracking-wide">{p.brand}</span>}
                        {p.imei && <span className="font-mono">IMEI {p.imei}</span>}
                        <span>{t('peripheral_picker_last_seen', 'Last seen')}: {relTime(p.lastSeen)}</span>
                      </div>
                      {disabled && (
                        <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          {t('peripheral_picker_no_imei', 'IMEI missing — cannot open peripheral window.')}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            )
          })}
        </div>

        {/* Footer: "Open" only when at least one card is checked. */}
        {selected.size > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {t('peripheral_picker_count_selected', '{{count}} selected', { count: selected.size })}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-4 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setSelected(new Set())}
              >
                {t('peripheral_picker_clear', 'Clear')}
              </button>
              <button
                type="button"
                className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                onClick={handleOpenChecked}
              >
                {t('peripheral_picker_open_btn', 'Open')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetPeripheralPicker
